import { Dangerous, Error } from "@mui/icons-material";
import { Box, CircularProgress, IconButton, List, ListItem, ListItemText, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import DOMPurify from "dompurify";
import { useEffect, useState } from "react";
import { MessageDeliveryType } from "../../../App/Enums/Message";
import { useCandidateMessageHistory, useReadMessage } from "../../../App/Queries/MessageQueries";
import { useCurrentUser } from "../../../App/Queries/UserQueries";
import { useUserProfileStore } from "../../../App/State/Stores/UserProfileStore";
import { ICandidate } from "../../../App/Typings/Candidate";
import { ICandidateProfile } from "../../../App/Typings/CandidateProfile";
import {
  ICandidateWithMessageInfo,
  IMessageHistory,
  IMessageScheduleQueue,
} from "../../../App/Typings/MessageText";
import { getDateUtc } from "../../../MessagingCenter/Utils";
import { DynamicMessageIcon, getMessageDisplayType } from "../../FunctionMethods/MessageFunctions";
import { Item } from "../../Styling/MessageStyling";
import { NoMessagesAlert, StopComplianceAlert } from "./Alerts";
import MessageActionsMenu from "../../../MessagingCenter/Components/MessageActionsMenu";

export default function MessageHistory({
  isStop,
  candidateId,
  candidate,
  temporaryMessages,
  onTempMessageReloadLoad,
  setReplyMessageId,
  setDisableNotification,
}: {
  isStop: boolean;
  candidateId: string;
  temporaryMessages?: IMessageScheduleQueue[];
  candidate?: ICandidate | ICandidateWithMessageInfo | ICandidateProfile | undefined;
  onTempMessageReloadLoad: () => void;
  setReplyMessageId: (id: string) => void;
  setDisableNotification?: (value: boolean) => void;
}) {
  const {
    data: messageHistoryList,
    isLoading,
    isError,
  } = useCandidateMessageHistory(candidateId, 1, 1000, MessageDeliveryType.Text);
  const { mutate: markMessageAsRead } = useReadMessage();
  const { id: userId } = useCurrentUser();
  const [markAsRead, setMarkAsRead] = useState<boolean>(false);

  const sameMessage = (msg: IMessageScheduleQueue) => {
    const lastMessage = messageHistoryList?.items[messageHistoryList.items.length - 1];

    if (lastMessage && lastMessage.messageDisplay.toLowerCase() === msg.bodyRaw.toLowerCase()) {
      return (
        getDateUtc(lastMessage.dateSort).format("DD-MM-YYYY-HH:mm") ===
        getDateUtc(msg.date).format("DD-MM-YYYY-HH:mm")
      );
    }

    return false;
  };

  useEffect(() => {
    if (
      !markAsRead &&
      messageHistoryList &&
      messageHistoryList.items &&
      messageHistoryList.items.filter((c) => !c.hasBeenRead).length > 0
    ) {
      markMessageAsRead(
        {
          messageIds: messageHistoryList.items.filter((c) => !c.hasBeenRead).map((c) => c.messageId),
          userId: userId,
        },
        {
          onSettled: () => {
            setMarkAsRead(false);
          },
        },
      );
    }

    if (temporaryMessages?.map((d) => sameMessage(d)).includes(true)) {
      onTempMessageReloadLoad();
    }

    if (messageHistoryList && messageHistoryList.items.length > 0) {
      const replies = messageHistoryList.items.filter((c) => c.messageType === "ReplyTextMessage");
      const lastReceivedMessage = replies.length
        ? replies.sort((a, b) => (a.dateSort.isAfter(b.dateSort) ? -1 : 1))[0]
        : undefined;
      if (lastReceivedMessage) {
        setReplyMessageId(lastReceivedMessage.messageId);
      }
      if (messageHistoryList.items.some((c) => c.userHasReplyNotification) && setDisableNotification) {
        setDisableNotification(true);
      } else {
        setDisableNotification?.(false);
      }
    }

    // reset read state on unmount
    return () => {
      setMarkAsRead(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageHistoryList]);

  if (isLoading || !messageHistoryList) {
    return (
      <Box sx={{ minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <span>Oops something went wrong...</span>;
  }

  if (!messageHistoryList.items.length) {
    if (isStop) {
      return <StopComplianceAlert />;
    }
    return <NoMessagesAlert />;
  }

  return (
    <List>
      {messageHistoryList.items.map((msg) => (
        <MessageHistoryListItem
          msg={msg}
          key={msg.messageId}
          candidate={candidate}
          onChange={() => setMarkAsRead(true)}
        />
      ))}
      {temporaryMessages &&
        temporaryMessages.map((msg) => (
          <MessageHistoryListItem
            isTemporary={true}
            msg={
              {
                dateSort: msg.date,
                messageDisplay: msg.bodyRaw,
                messageType: "SystemTextMessage",
                isScheduled: false,
              } as IMessageHistory
            }
            key={"temp-message-id"}
            candidate={candidate}
          />
        ))}
      {isStop ? <MessageHistoryListItem msg={StopMessageItem} /> : null}
    </List>
  );
}

const StopMessageItem: IMessageHistory = {
  created: dayjs().utc(),
  candidateCount: 0,
  type: "Stop",
  scheduledUtcTime: dayjs().utc(),
  dateSort: dayjs().utc(),
  messageTrackingId: "stop",
  isScheduled: false,
  messageId: "stop",
  messageType: "Stop",
  messageDisplay: "Unable to send text — candidate has replied with a stop phrase",
  messageState: "ERROR",
  scheduledTimeEastern: "N/A",
  userName: "N/A",
  groupName: "N/A",
  errorMessage: "STOP Compliance",
  hasBeenRead: false,
  userHasReplyNotification: false,
};

export function MessageHistoryListItem({
  msg,
  isTemporary = false,
  candidate,
  showDate = true,
  onChange,
}: {
  msg: IMessageHistory;
  isTemporary?: boolean;
  candidate?: ICandidate | ICandidateWithMessageInfo | ICandidateProfile | undefined;
  showDate?: boolean;
  onChange?: () => void;
}) {
  const { getUserAdjustedDate } = useUserProfileStore();
  const isUserSentMessage = getMessageDisplayType(msg.messageType) === "send";
  const isStopMessage = msg.messageType === "Stop";
  const messageFlexDirection = isUserSentMessage ? "row-reverse" : "row";
  const messageColor = isTemporary
    ? "#aaa"
    : isStopMessage
      ? "#ed7044"
      : isUserSentMessage
        ? "#488bf7"
        : "#818996";

  const userSentStyle = {
    content: '""',
    position: "absolute",
    display: "block",
    width: 0,
    zIndex: 1,
    borderStyle: "solid",
    borderWidth: "0 10px 10px 0",
    borderColor: `transparent ${messageColor} transparent transparent`,
    top: "58%",
    left: "30px",
    marginTop: "-10px",
  };

  const sentStyle = {
    content: '""',
    position: "absolute",
    display: "block",
    width: 0,
    zIndex: 1,
    borderStyle: "solid",
    borderWidth: "10px 10px 0 0",
    borderColor: `${messageColor} transparent transparent transparent`,
    top: "58%",
    right: "30px",
    marginTop: "-10px",
  };

  return (
    <ListItem
      sx={{
        m: "0px",
        display: "block",
        px: 0,
        py: "0.25em",
      }}
      data-test={`text-message-history-item-${msg.messageId}`}
    >
      <Box
        component="span"
        sx={{
          display: "flex",
          flexDirection: messageFlexDirection,
          alignItems: "center",
          gap: "0.5ch",
          margin: 0,
          padding: 0,
        }}
      >
        <IconButton
          data-test={`message-icon-${msg.messageId}`}
          size="small"
          disableRipple={true}
          sx={{ cursor: "default" }}
        >
          <DynamicMessageIcon
            messageType={msg.messageType}
            user={
              !isUserSentMessage && candidate
                ? `${candidate?.firstName} ${candidate?.lastName}`
                : msg.userName
            }
            groupName={msg.groupName}
          />
        </IconButton>

        <Item
          data-test={`message-${msg.messageId}-type-${msg.messageType}`}
          sx={{
            padding: "0.5rem 0.75rem",
            fontWeight: "normal",
            backgroundColor: messageColor,
            color: "white",
            margin: "0px",
            maxWidth: "70%",
            "&::after": isUserSentMessage ? sentStyle : userSentStyle,
            minHeight: "25px",
            minWidth: "35px",
            "@keyframes pulse": {
              "0%": {
                opacity: 1,
              },
              "50%": {
                opacity: 0.75,
              },
              "100%": {
                opacity: 1,
              },
            },
            animation: isTemporary ? "pulse 1.2s infinite linear" : "none",
            "& a": {
              color: "white",
              textDecoration: "underline",
              display: "inline-block",
              borderRadius: "3px",
              fontWeight: 600,
              padding: "0 0.3ch 0.1em",
              transition: "background-color 0.2s",
            },
            "& a:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.2)",
            },
            "& a:focused": {
              backgroundColor: "rgba(0, 0, 0, 0.2)",
            },
            "& a:visited": {
              color: "white",
            },
          }}
        >
          <MessageDisplay message={msg.messageDisplay} />
        </Item>
        {msg.messageState === "ERROR" || msg.messageState === "UNDELIVERED" ? (
          <Tooltip
            title={msg.messageState === "UNDELIVERED" ? "Undelivered" : msg.errorMessage || "Unknown Error"}
          >
            {msg.messageState === "UNDELIVERED" ? (
              <Dangerous data-test={`message-error-${msg.messageId}`} color="error" />
            ) : (
              <Error data-test={`message-error-${msg.messageId}`} color="error" />
            )}
          </Tooltip>
        ) : null}
        {msg.type === "ReplyTextMessage" && (
          <MessageActionsMenu
            messageId={msg.messageId}
            hasBeenRead={msg.hasBeenRead}
            size="small"
            onChange={onChange}
            messageType="sms"
          />
        )}
      </Box>
      {showDate && (
        <ListItemText
          sx={{
            display: "flex",
            flexDirection: messageFlexDirection,
            alignItems: "center",
            gap: "0.5ch",
            margin: "0 1px 0 5px",
            padding: 0,
            marginRight: 1,
            " > .MuiListItemText-secondary": {
              fontSize: "10px",
              color: "#aaa",
            },
          }}
          secondary={getUserAdjustedDate(msg.dateSort).format("LLL")}
        />
      )}
    </ListItem>
  );
}

// (?:https?:\/\/)? - optional http(s)://
// (?:([\w-]{2,})+\.)* - optional subdomain at least 2 letters, can be any word character or - followed by . character 0 or more times
// ([\w-]{2,})+\.[a-z]{2,} - required top level domain at least 2 letters, any word character or - followed by . and at least 2 letters
// (?:[\w/\-&?=%#]*)? - optional path, can be any word character, /, -, &, =, %, or #, 0 or more times
const textLinkRegex = /(?:https?:\/\/)?(?:([\w-]{2,})+\.)*([\w-]{2,})+\.[a-z]{2,}(?:[\w/\-&?=%#]*)?/g;
function makeLinksClickable(text: string) {
  return text.replace(textLinkRegex, (url) => {
    const linkUrl = url.startsWith("http") ? url : `//${url}`;
    return `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer" title="Open link in new tab">${url}</a>`;
  });
}

function MessageDisplay({ message }: { message: string }) {
  const createMarkup = () => {
    const textWithLink = makeLinksClickable(message);
    return { __html: DOMPurify.sanitize(textWithLink, { ADD_ATTR: ["target"] }) };
  };
  return <span dangerouslySetInnerHTML={createMarkup()} />;
}
